import api from './api';

import { uploadFileToPresignedUrl as uploadFileApi } from './api';
// --- INICIO DE LA CORRECCIÓN ---
/**
 * Limpia un nombre de archivo de caracteres problemáticos (incluidos emojis).
 * @param {string} filename - El nombre del archivo original.
 * @returns {string} - El nombre del archivo sanitizado.
 */
const sanitizeFilename = (filename) => {
  if (!filename) return 'unnamed-file';

  const parts = filename.split('.');
  // Si no hay extensión, o el nombre es algo como ".config", tratarlo como sin extensión
  if (parts.length < 2 || (parts[0] === '' && parts.length === 2)) {
    const name = parts.join('.'); // Unir de nuevo
    return (
      name
        .replace(/[\s_&,]+/g, '-') // Reemplazar espacios, _, &, , con guiones
        // Eliminar todo lo que no sea letra, número o guion
        .replace(/[^a-zA-Z0-9-]/g, '')
        .replace(/-+/g, '-') // Consolidar guiones
        .replace(/^-+|-+$/g, '') || 'file' // Quitar guiones al inicio/fin
    );
  }

  const extension = parts.pop();
  const name = parts.join('.');

  const sanitizedName = name
    .replace(/[\s_&,]+/g, '-') // Reemplazar espacios, _, &, , con guiones
    // Eliminar todo lo que no sea letra, número o guion
    .replace(/[^a-zA-Z0-9-]/g, '')
    .replace(/-+/g, '-') // Consolidar guiones
    .replace(/^-+|-+$/g, ''); // Quitar guiones al inicio/fin

  return `${sanitizedName || 'file'}.${extension}`;
};
// --- FIN DE LA CORRECCIÓN ---

/**
 * Solicita una URL firmada y temporal a la API para un recurso protegido en MinIO.
 * (Función existente - Sin cambios)
 */
export const generateSignedMediaUrl = async (
  contentType,
  contentId,
  mediaKey
) => {
  try {
    if (!contentType || !contentId || !mediaKey) {
      return '/images/default-post-image-error.png';
    }
    const payload = { contentType, contentId, mediaKey };
    const { data } = await api.post(
      '/media-access/generate-signed-url',
      payload
    );
    if (data && typeof data.signedUrl === 'string' && data.signedUrl) {
      return data.signedUrl;
    } else {
      throw new Error(
        'La respuesta del servidor no contenía una URL firmada válida.'
      );
    }
  } catch (error) {
    if (error.response?.status !== 403) {
      console.error(
        `Error al obtener la URL firmada para la clave "${mediaKey}":`,
        error.response?.data || error.message
      );
    }
    return '/images/default-post-image-error.png';
  }
};

/**
 * Obtiene la previsualización de un efecto.
 * (Función existente - Sin cambios)
 */
export const getEffectPreview = async (
  sourceFile,
  sourceKey,
  effectType,
  effectIntensity
) => {
  try {
    const formData = new FormData();
    formData.append('effectType', effectType);
    formData.append('effectIntensity', String(effectIntensity));

    if (sourceFile) {
      formData.append('previewFile', sourceFile);
    } else if (sourceKey) {
      formData.append('sourceKey', sourceKey);
    } else {
      throw new Error(
        'Se requiere un archivo o una clave de origen para la previsualización.'
      );
    }

    const response = await api.post(`/media/preview-effect`, formData, {
      responseType: 'blob',
    });

    return URL.createObjectURL(response.data);
  } catch (error) {
    console.error('Error en getEffectPreview:', error.response?.data || error);
    throw (
      error.response?.data || new Error('Error al generar la previsualización.')
    );
  }
};

// --- 2. NUEVAS FUNCIONES PARA SUBIDA DIRECTA ---

/**
 * Paso 1: Solicita al backend una URL firmada (PUT) para subir un archivo.
 * (Función existente - Sin cambios)
 */
export const requestPresignedUrl = async (file, context, contextId) => {
  const sanitizedFileName = sanitizeFilename(file.name);

  const payload = {
    fileName: sanitizedFileName,
    contentType: file.type,
    fileSize: file.size,
    context: context,
    contextId: contextId,
  };
  try {
    const { data } = await api.post('/media/presigned-upload', payload);
    return data; // Devuelve { presignedUrl, objectKey }
  } catch (error) {
    console.error('Error al solicitar la URL firmada:', error.response?.data);
    throw error.response?.data || new Error('Error al preparar la subida.');
  }
};

/**
 * Paso 2: Sube el archivo directamente a MinIO usando la URL firmada.
 * (Función existente - Sin cambios)
 */
export const uploadFileDirectly = async (
  presignedUrl,
  file,
  onUploadProgress
) => {
  // Llama a la función importada desde 'api.js'
  const result = await uploadFileApi(presignedUrl, file, onUploadProgress);
  if (!result.success) {
    throw result.error; // Lanza el error para que React Query lo capture
  }
  return result;
};

/**
 * Paso 3: Confirma al backend que la subida fue exitosa.
 * @param {string} objectKey - La clave del objeto subido.
 * @param {string} context - 'posts', 'packs', etc.
 * @param {string} contextId - El ID del post o pack.
 * @param {object} metadata - Metadatos adicionales (ej: { title, effect }).
 * @param {boolean} shouldQueueWorker - (NUEVO) Indica si esta es la última llamada y debe encolar el job.
 * @returns {Promise<any>}
 */
export const confirmUpload = async (
  objectKey,
  context,
  contextId,
  metadata,
  shouldQueueWorker = false // <-- VALOR POR DEFECTO AÑADIDO
) => {
  const payload = {
    objectKey,
    context,
    contextId,
    metadata,
    shouldQueueWorker, // <-- NUEVO CAMPO AÑADIDO AL PAYLOAD
  };
  try {
    const { data } = await api.post('/media/confirm-upload', payload);
    return data;
  } catch (error) {
    console.error('Error al confirmar la subida:', error.response?.data);
    throw error.response?.data || new Error('Error al confirmar la subida.');
  }
};

/**
 * Pide al backend una URL pre-firmada para subir un archivo de lección.
 */
export const getLessonUploadUrl = async (
  courseId,
  moduleId,
  lessonId,
  file
) => {
  const { data } = await api.post('/media/presigned-lesson-upload', {
    courseId,
    moduleId,
    lessonId,
    filename: file.name,
    contentType: file.type,
    // --- ESTA LÍNEA ES CRUCIAL ---
    size: file.size,
  });
  return data; // { presignedUrl, objectKey }
};

/**
 * Solicita una URL prefirmada para subir el contenido de una lección (video/archivo).
 * @param {string} courseId - ID del curso.
 * @param {string} moduleId - ID del módulo.
 * @param {string} lessonId - ID de la lección.
 * @param {File} file - El archivo a subir.
 * @returns {Promise<{ presignedUrl: string, objectKey: string }>} URL y clave del objeto.
 */
export const getPresignedLessonUploadUrl = async (
  courseId,
  moduleId,
  lessonId,
  file
) => {
  if (!file) throw new Error('No file provided for upload.');
  try {
    const response = await api.post('/media/presigned-lesson-upload', {
      courseId,
      moduleId,
      lessonId,
      filename: file.name,
      contentType: file.type,
    });
    return response.data; // { presignedUrl, objectKey }
  } catch (error) {
    console.error(
      'Error getting presigned URL for lesson:',
      error.response?.data || error
    );
    throw (
      error.response?.data ||
      new Error('Error al obtener URL para subir lección')
    );
  }
};

/**
 * Confirma que el contenido de una lección ha sido subido correctamente a MinIO.
 * @param {string} courseId - ID del curso.
 * @param {string} moduleId - ID del módulo.
 * @param {string} lessonId - ID de la lección.
 * @param {string} objectKey - La clave del objeto subido a MinIO.
 * @param {string} originalFilename - El nombre original del archivo.
 * @param {string} contentType - El tipo MIME del archivo.
 * @param {number} size - El tamaño del archivo en bytes.
 * @returns {Promise<object>} La lección actualizada.
 */
export const confirmLessonUpload = async (
  courseId,
  moduleId,
  lessonId,
  objectKey,
  originalFilename,
  contentType,
  size
) => {
  const { data } = await api.post('/media/confirm-lesson-upload', {
    courseId,
    moduleId,
    lessonId,
    objectKey,
    originalFilename,
    contentType,
    size,
  });
  return data; // Devuelve la lección actualizada
};
/**
 * Requests a presigned URL for uploading a course cover image.
 * @param {string} courseId - The ID of the course.
 * @param {File} file - The image file to upload.
 * @returns {Promise<{ presignedUrl: string, objectKey: string }>} URL and key of the object (original in private bucket).
 */
export const getPresignedCourseCoverUploadUrl = async (courseId, file) => {
  if (!file) throw new Error('No file provided for upload.');
  try {
    const response = await api.post('/media/presigned-course-cover-upload', {
      courseId,
      filename: file.name,
      contentType: file.type,
      size: file.size,
    });
    return response.data; // { presignedUrl, objectKey }
  } catch (error) {
    console.error(
      'Error getting presigned URL for course cover:',
      error.response?.data || error
    );
    throw (
      error.response?.data ||
      new Error('Error al obtener URL para subir portada')
    );
  }
};

/**
 * Confirms that the course cover image has been uploaded correctly to MinIO's private bucket.
 * @param {string} courseId - ID of the course.
 * @param {string} objectKey - The key of the uploaded original object in the private bucket.
 * @param {string} contentType - The MIME type of the uploaded file.
 * @param {number} size - The size of the uploaded file in bytes.
 * @returns {Promise<object>} The updated course data subset (e.g., { _id, coverImage }).
 */
export const confirmCourseCoverUpload = async (
  courseId,
  objectKey,
  contentType,
  size
) => {
  const { data } = await api.post('/media/confirm-course-cover-upload', {
    courseId,
    objectKey,
    contentType,
    size,
  });
  return data; // Devuelve el objeto de portada actualizado
};
/**
 * Pide al backend una URL pre-firmada para subir una portada de curso.
 */
export const getCourseCoverUploadUrl = async (courseId, file) => {
  const { data } = await api.post('/media/presigned-course-cover-upload', {
    courseId,
    filename: file.name,
    contentType: file.type,
    // --- ESTA LÍNEA ES CRUCIAL ---
    size: file.size,
  });
  return data; // { presignedUrl, objectKey }
};
